"use client";

import { useTranslations } from "next-intl";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { Button, Card, IconButton, Typography } from "@mui/material";
import { red } from "@/theme/theme-primitives";
import BackupRoundedIcon from "@mui/icons-material/BackupRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import { useRef, useState } from "react";
import {
  HiddenInput,
  UploadContainer,
  UploadContent,
  UploadCounter,
} from "./common.style";

const WithCard = ({ heading, children, withoutCard = false }) => {
  const t = useTranslations();
  if (withoutCard) return children;

  return (
    <Card sx={{ gap: "2rem", width: "100%" }} elevation={1}>
      <Typography variant="h6">{heading}</Typography>
      {children}
    </Card>
  );
};

export const UploadFile = ({
  required = false,
  heading,
  withoutCard = false,
  onUploadFile,
  files = [],
  maxFiles = 10,
  acceptedFiles = ".pdf,.jpg,.jpeg,.png",
  maxFileSizeMB = 5,
}) => {
  const t = useTranslations();
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [fileUploadError, setFileUploadError] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (e) => {
    if (e.target.files) {
      addNewFiles(e.target.files);
    }
  };

  const addNewFiles = (fileList) => {
    setIsUploading(true);

    setTimeout(() => {
      if (files.length >= maxFiles) {
        setFileUploadError(`You can only upload a maximum of ${maxFiles} files.`);
        setIsUploading(false);
        return;
      }

      const newFiles = [];
      const filesToAdd = Array.from(fileList).slice(0, maxFiles - files.length);

      for (const file of filesToAdd) {
        const id = `file-${Date.now()}`;

        if (file.size / 1024 / 1024 > maxFileSizeMB) {
          setFileUploadError(`File size exceeds ${maxFileSizeMB}MB. Please select a smaller file.`);
          continue;
        }

        if (!acceptedFiles.split(",").some((ext) => file.name.endsWith(ext.trim()))) {
          setFileUploadError(
            `Invalid file type. Supported formats: ${acceptedFiles
              .split(",")
              .map((ext) => ext.trim())
              .join(", ")}`
          );
          continue;
        }

        setFileUploadError("");
        newFiles.push({
          id,
          file,
          preview: URL.createObjectURL(file),
        });
      }

      const updatedImages = [...files, ...newFiles];
      onUploadFile(updatedImages);
      setIsUploading(false);
    }, 500); // simulate delay
  };

  const removeFile = (e, id) => {
    e.stopPropagation();
    const updatedFiles = files.filter((file) => file.id !== id);
    onUploadFile(updatedFiles);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      addNewFiles(e.dataTransfer.files);
    }
  };

  const openFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <WithCard withoutCard={withoutCard} heading={heading}>
      <div
        className={`relative w-full border-2 ${
          isDragging ? "border-blue-500" : "border-gray-300"
        } border-dashed rounded-lg p-6 transition-colors duration-200`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <HiddenInput
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept={acceptedFiles}
          multiple
        />

        {/* Loader Overlay */}
        {isUploading && (
          <div className="absolute inset-0 z-10 bg-white bg-opacity-70 flex items-center justify-center">
            <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        <UploadContent className="cursor-pointer">
          <BackupRoundedIcon color="action" />
          {withoutCard && heading && (
            <Typography
              sx={{
                color: "#4B5563",
                fontSize: "16px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
              variant="body1"
            >
              {heading} {required && <span className="text-red-500">*</span>}
            </Typography>
          )}
          <Typography
            sx={{ color: "#4B5563", fontSize: "16px" }}
            variant="body1"
          >
            {t("Drag  Drop or Click to Upload")}
          </Typography>
          <Typography color="light-grey" variant="body1">
            {t("Supported formats")}{" "}
            {acceptedFiles
              ?.split?.(",.")
              ?.map?.((file) => file?.toUpperCase())
              ?.join?.(" ")
              ?.substring?.(1)}{" "}
            {t("Max")}
            {maxFileSizeMB}
            {t("MB")}
          </Typography>
          <Button color="primary" variant="subdued" size="small">
            {t("Choose File")}
          </Button>
          <UploadCounter>
  {!files?.length && (
    <Typography
      sx={{
        color: red[400], // red from your theme
        fontWeight: 400,
      }}
      variant="body2"
    >
      <b>{t("Selected files")}</b> {t("None")}
    </Typography>
  )}

  {fileUploadError && (
    <Typography
      sx={{ color: red[500], fontWeight: 600 }}
      variant="body2"
    >
      {fileUploadError}
    </Typography>
  )}
</UploadCounter>


          {files?.map(({ file, id }) => (
            <Card
              key={id}
              elevation={1}
              sx={{
                background: "hsla(163, 94%, 70%, 0.4)",
                padding: "0.5rem 1rem",
                color: "#047857",
                width: "100%",
              }}
            >
              <div className="flex justify-between items-center">
                <div className="flex gap-2 items-center">
                  {file?.name}
                  <CheckCircleRoundedIcon color="primary" />
                </div>
                <IconButton onClick={(e) => removeFile(e, id)}>
                  <ClearRoundedIcon color="error" />
                </IconButton>
              </div>
            </Card>
          ))}
        </UploadContent>
      </div>
    </WithCard>
  );
};

export default UploadFile;
